import React, { useEffect, useState, createContext, useContext } from 'react';
import { useCRM } from './CRMContext';
export type CustomerProfile = {
  phone: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  birthday?: string;
  points?: number;
  visits?: number;
};
type AuthStatus = 'guest' | 'verified';
type Ctx = {
  customer: CustomerProfile | null;
  // A customer is fully authenticated only once they have completed signup
  // (i.e. they have a name). A verified-but-nameless customer is mid-signup.
  isAuthenticated: boolean;
  sendOtp: (phone: string) => Promise<void>;
  // Returns true if this is a brand-new user who still needs to provide a name.
  verifyOtp: (code: string) => Promise<{
    isNewUser: boolean;
  }>;
  completeSignup: (firstName: string, lastName: string, birthday: string) => void;
  logout: () => void;
  awardPoints: (total: number) => void;
};
const CustomerAuthContext = createContext<Ctx | null>(null);
// Demo "known" customers so the OTP flow can branch into sign-in vs sign-up.
const KNOWN_CUSTOMERS: Record<string, CustomerProfile> = {
  '+251911223344': {
    phone: '+251911223344',
    name: 'Selam Tesfaye',
    firstName: 'Selam',
    lastName: 'Tesfaye',
    birthday: '1994-03-12',
    points: 120,
    visits: 4
  }
};
const STORAGE_KEY = 'mp_customer_auth';
function loadInitial(): CustomerProfile | null {
  if (typeof window === 'undefined') return KNOWN_CUSTOMERS['+251911223344'];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return KNOWN_CUSTOMERS['+251911223344'];
    return JSON.parse(raw);
  } catch {
    return KNOWN_CUSTOMERS['+251911223344'];
  }
}
export function CustomerAuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(loadInitial);
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  const {
    captureLead
  } = useCRM();
  useEffect(() => {
    try {
      if (customer) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(customer));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [customer]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        if (!e.newValue) {
          setCustomer(null);
        } else {
          setCustomer(JSON.parse(e.newValue));
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const sendOtp = async (phone: string) => {
    // Simulated network latency for the OTP dispatch.
    await new Promise(r => setTimeout(r, 600));
    setPendingPhone(phone);
  };
  const verifyOtp = async (_code: string) => {
    await new Promise(r => setTimeout(r, 500));
    const phone = pendingPhone || '';
    const known = KNOWN_CUSTOMERS[phone];
    if (known) {
      setCustomer(known);
      return {
        isNewUser: false
      };
    }
    // New user: verified, but no profile yet.
    setCustomer({
      phone
    });
    captureLead(phone);
    return {
      isNewUser: true
    };
  };
  const completeSignup = (firstName: string, lastName: string, birthday: string) => {
    const fullName = `${firstName} ${lastName}`.trim();
    const phone = customer?.phone || pendingPhone || '';
    setCustomer(prev => prev ? {
      ...prev,
      name: fullName,
      firstName,
      lastName,
      birthday
    } : {
      phone,
      name: fullName,
      firstName,
      lastName,
      birthday
    });
    if (phone) {
      captureLead(phone, {
        firstName,
        lastName,
        birthDate: birthday || undefined
      });
    }
  };
  const logout = () => {
    setCustomer(null);
    setPendingPhone(null);
  };
  const awardPoints = (total: number) => {
    if (!customer) return;
    const earned = Math.floor(total / 10);
    setCustomer(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        points: (prev.points || 0) + earned,
        visits: (prev.visits || 0) + 1
      };
    });
  };
  const isAuthenticated = !!customer?.name;
  return <CustomerAuthContext.Provider value={{
    customer,
    isAuthenticated,
    sendOtp,
    verifyOtp,
    completeSignup,
    logout,
    awardPoints
  }} data-id="element-1519">
      {children}
    </CustomerAuthContext.Provider>;
}
export function getLoyaltyTier(points: number = 0) {
  if (points >= 500) return {
    name: 'Gold',
    color: 'amber',
    next: null
  };
  if (points >= 200) return {
    name: 'Silver',
    color: 'slate',
    next: 500
  };
  return {
    name: 'Bronze',
    color: 'orange',
    next: 200
  };
}
export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
}